/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import axios from 'axios';
import type { InternalAxiosRequestConfig, Axios } from 'axios';
import { HTTPMethod } from 'http-method-enum';
import { StatusCodes } from 'http-status-codes';
import urlJoin from 'proper-url-join';
import { v4 as uuidv4 } from 'uuid';

import {
  ipAddressRestriction,
  sessionInactive,
  sessionRevoked
} from '@ibm-watson/shared/modules/ia/store/session/actions';
import {
  getAccount,
  getTenant,
  selectCsrfToken,
  selectSessionInvalid
} from '@ibm-watson/shared/modules/ia/store/session/selectors';
import { isWxoEnabled } from '@ibm-watson/shared/modules/ia/util/config';
import {
  isCrn,
  parse as parseCrn
} from '@ibm-watson/shared/modules/ia/util/crn';
import history, {
  doLogin,
  doLogout
} from '@ibm-watson/shared/modules/ia/util/history';
import {
  isAuthSessionInactive,
  isAuthSessionIpAddressRestriction,
  isAuthSessionRevoked
} from '@ibm-watson/shared/modules/ia/util/rest';
import { extractTenant } from '@ibm-watson/shared/modules/ia/util/url';
import { Header } from '@ibm-watson/shared/ts/header';
import { MfePrefix } from '@ibm-watson/shared/ts/mfe';
import { AuthErrorMessages } from '../../../ts/authErrors';
import { ELEVENLABS_API_BASE_ENDPOINT } from '../../channels/api/speech';
import {
  selectIsElevenlabsEnabled,
  selectIsGenesysAudioConnectorInCpd
} from '../store/features/selectors';

const isCreatingSpeech = error => {
  const url = error?.config?.url;
  return (
    /speech_to_text|text_to_speech/i.test(url) &&
    error?.config?.method === HTTPMethod.POST.toLowerCase()
  );
};

const isTwilioSubscribe = error => {
  const url = error?.config?.url;
  return (
    /twilio|twiliowhatsapp/i.test(url) &&
    /subscribe/i.test(url) &&
    error?.config?.method === HTTPMethod.POST.toLowerCase()
  );
};

const isIntelepeerSubscribe = error => {
  const url = error?.config?.url;
  return (
    /intelepeer/i.test(url) &&
    /subscribe/i.test(url) &&
    error?.config?.method === HTTPMethod.POST.toLowerCase()
  );
};

const isPhoneSubscribe = error => {
  const url = error?.config?.url;
  return (
    /phone/i.test(url) &&
    /subscribe/i.test(url) &&
    error?.config?.method === HTTPMethod.POST.toLowerCase()
  );
};

const isCatalogIntegrationsCreate = error => {
  const url = error?.config?.url;
  return (
    /catalog\/integrations/i.test(url) &&
    /create/i.test(url) &&
    error?.config?.method === HTTPMethod.POST.toLowerCase()
  );
};

const isServiceDeskSubscribe = error => {
  const url = error?.config?.url;
  return (
    /service_desks/i.test(url) &&
    /subscribe/i.test(url) &&
    error?.config?.method === HTTPMethod.POST.toLowerCase()
  );
};

const isCPDPhoneIntegration = error => {
  const url = error?.config.url;
  return /cpd\/stt/i.test(url) && /cpd\/tts/i.test(url);
};

const isElevenLabsTTSPhoneIntegration = error => {
  const url = error?.config.url;
  return url.includes(ELEVENLABS_API_BASE_ENDPOINT);
};
/**
 * Function that configures the Axios interceptors for requests and responses
 * for the specified Axios instance.
 * When Tooling is running as an MFE, it prepends the appropriate context
 * root prefix to the base URL, which is needed for proxying purposes.
 *
 * @param axiosInstance The Axios instance to configure
 */
export const configureAxiosInstance = (
  axiosInstance: Axios,
  getStore: () => any
): void => {
  axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // It is OK to get the state once and reuse the same object for all
      // subsequent invocations because this method does not issue any actions
      // that might change the state.
      // FIXME: remove the cast to never once the store is fully typed
      const state = getStore().getState() as never;

      const { region, instance } = getTenant(state);
      if (region && instance) {
        if (isCrn(instance)) {
          const parsed = parseCrn(instance);
          config.headers[
            Header.X_WATSON_UI_INFO
          ] = `bluemix-instance-id=${parsed.service_instance};bluemix-region-id=${region};bluemix-crn=${instance}`;
        } else {
          config.headers[
            Header.X_WATSON_UI_INFO
          ] = `bluemix-instance-id=${instance};bluemix-region-id=${region}`;
        }
      }

      config.headers[Header.X_GLOBAL_TRANSACTION_ID] = uuidv4();

      // Retrieve the CSRF token retrieved after the user was authenticated
      // and add a custom header to the request. For certain HTTP methods
      // that can cause changes, the server will make sure that the CSRF
      // token specified matches the value that it assigned for this particular
      // user session.
      const csrfToken = selectCsrfToken(state);
      config.headers[Header.X_CSRF_TOKEN] = csrfToken;

      // If the user has become invalid (e.g. it has timed out or been revoked),
      // there is no need to send any more requests, so we abort them.
      let signal = undefined;
      // FIXME: remove the cast to never once the store is fully typed
      if (selectSessionInvalid(state)) {
        console.debug(
          'Abort request, authentication session has become invalid'
        );
        const controller = new AbortController();
        controller.abort();
        signal = controller.signal;
      }

      return {
        ...config,
        signal,
        // If we need a context root prefix for all API calls (e.g. if we are
        // running as an MFE), prepend that to the API URL.
        baseURL: isWxoEnabled ? MfePrefix.ASSISTANTS : undefined
      };
    }
  );

  axiosInstance.interceptors.response.use(
    response => response,
    error => {
      if (error) {
        error.transactionId =
          error.config?.headers[Header.X_GLOBAL_TRANSACTION_ID] || undefined;
      }
      const httpStatus = error.response?.status;
      let responseData = error.response?.data;

      const isGenesysAudioConnectorEnabledInCpd = Boolean(
        selectIsGenesysAudioConnectorInCpd(getStore().getState() as never)
      );

      const isElevenLabsTTSEnabled = Boolean(
        selectIsElevenlabsEnabled(getStore().getState() as never)
      );

      if (responseData) {
        // arraybuffer can occur on image fetch (e.g. `screenshot` in ia/api/preview)
        if (error.response.config?.responseType === 'arraybuffer') {
          const decoder = new TextDecoder();
          const dStr = decoder.decode(responseData);
          // update both error.response.data (for toast display) and responseData in case encountered on FORBIDDEN
          responseData = JSON.parse(dStr);
          error.response.data = responseData;
        }
      }

      if (
        httpStatus === StatusCodes.FORBIDDEN &&
        !isCreatingSpeech(error) &&
        !isTwilioSubscribe(error) &&
        !isIntelepeerSubscribe(error) &&
        !isPhoneSubscribe(error) &&
        !isCatalogIntegrationsCreate(error) &&
        !isServiceDeskSubscribe(error)
      ) {
        history.push(urlJoin(document.baseURI, 'unauthorized'), {
          error: responseData
        });
      }

      /*
        In CPD, the GenesysAudioConnector makes api calls to speech to text
        and text to speech instances. Those calls may return Unauthorized, but
        that should not cause Tooling to log out, so we reject that case.
      */
      if (
        isGenesysAudioConnectorEnabledInCpd &&
        (httpStatus === StatusCodes.UNAUTHORIZED ||
          httpStatus === StatusCodes.FORBIDDEN) &&
        isCPDPhoneIntegration(error)
      ) {
        return Promise.reject(error);
      }

      /*
        For cloud the GenesysAudioConnector 11Labs TTS provider makes api calls to the endpoints. 
        Those calls may return Unauthorized, but hat should not cause Tooling to log out, so we reject that case.
      */
      if (
        isElevenLabsTTSEnabled &&
        (httpStatus === StatusCodes.UNAUTHORIZED ||
          httpStatus === StatusCodes.FORBIDDEN) &&
        isElevenLabsTTSPhoneIntegration(error)
      ) {
        return Promise.reject(error);
      }

      // Creations of catalog integrations and service desk subscription API
      // calls may return Unauthorized, but that should not cause Tooling to
      // log out, so we will ignore those cases.
      // Also, in WXO we do not force logouts, since the WXO home page is
      // in charge of refreshing the auth token.
      if (
        httpStatus === StatusCodes.UNAUTHORIZED &&
        !isGenesysAudioConnectorEnabledInCpd &&
        !isWxoEnabled &&
        !isCatalogIntegrationsCreate(error) &&
        !isServiceDeskSubscribe(error)
      ) {
        const isAccountAvailable = Boolean(
          getAccount(getStore().getState() as never)
        );

        // If an API received an unauthorized error after the page has loaded,
        // it must mean our authenticated session has expired, so trigger
        // a logout to clear the session information from the server.
        // We should not perform a logout when NO_SESSION_FOUND is returned. This
        // can happen when there is no session found in Redis but the access token
        // is still valid
        if (
          isAccountAvailable &&
          responseData.message !== AuthErrorMessages.NO_SESSION_FOUND
        ) {
          doLogout();
          return;
        }

        // If account information is not available, that means that the page
        // just loaded and the user simply has not been authenticated yet:
        // in this case, trigger a login.
        // FIXME: remove the cast to never once the store is fully typed
        const instance =
          getTenant(getStore().getState() as never)?.instance ||
          extractTenant(window.location.pathname)?.instance;

        doLogin(instance);
      } else if (isAuthSessionInactive(error)) {
        getStore().dispatch(sessionInactive());
      } else if (isAuthSessionRevoked(error)) {
        getStore().dispatch(sessionRevoked());
      } else if (isAuthSessionIpAddressRestriction(error)) {
        getStore().dispatch(ipAddressRestriction());
      }

      return Promise.reject(error);
    }
  );
};

/**
 * Sets up the global Axios instance and configures its request and response
 * interceptors.
 */
export const apiSetup = (getStore: () => any): void => {
  // Add a request interceptor
  configureAxiosInstance(axios, getStore);
};
